import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// ---------------- Fetch all job titles ----------------
export const fetchJobTitles = createAsyncThunk(
  'jobTitle/fetchJobTitles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/job-title`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch job titles';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ---------------- Create / Update job title ----------------
export const saveJobTitle = createAsyncThunk(
  'jobTitle/saveJobTitle',
  async (jobData, { dispatch, rejectWithValue }) => {
    try {
      // Clean up formData: remove empty strings, null, or undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(jobData).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      );

      const response = await axios.post(`${API_URL}/job-title`, cleanedData);
      dispatch(fetchJobTitles()); // Refresh list after save
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save job title';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);
// ---------------- Update job title via PATCH ----------------
export const updateJobTitle = createAsyncThunk(
  'jobTitle/updateJobTitle',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/job-title/${id}`, data);
      dispatch(fetchJobTitles());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update job title';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ---------------- Toggle active status ----------------
export const toggleJobTitleStatus = createAsyncThunk(
  'jobTitle/toggleJobTitleStatus',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/job-title/${id}/toggle`);
      dispatch(fetchJobTitles());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to toggle status';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

const jobTitleSlice = createSlice({
  name: 'jobTitle',
  initialState: {
    list: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    clearJobTitleError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ----- Fetch -----
      .addCase(fetchJobTitles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchJobTitles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
        state.error = null;
      })
      .addCase(fetchJobTitles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ----- Save (Create) -----
      .addCase(saveJobTitle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(saveJobTitle.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(saveJobTitle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ----- Update -----
      .addCase(updateJobTitle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateJobTitle.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(updateJobTitle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ----- Toggle status -----
      .addCase(toggleJobTitleStatus.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(toggleJobTitleStatus.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(toggleJobTitleStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearJobTitleError } = jobTitleSlice.actions;
export default jobTitleSlice.reducer;
